import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { provincesCode } from "@/constants/provinces";
import { StandaloneSearchBox, useLoadScript } from "@react-google-maps/api";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { SiteDefaultValues, SiteSchema, TSiteSchema } from "../Models/Site";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-toastify";
import { Label } from "@/components/ui/label";
import { appendSiteAddress } from "@/utils/helpers";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { provinces } from "@/constants/provinces";
import { useTranslation } from "react-i18next";
import { CustomToastComponent } from "@/components/shared";
import { cn } from "@/lib/utils";

/**
 * Libraries to load for the Google Maps API
 * Decides what data to load from the Google Maps API
 */
const libraries: ("places")[] = ["places"];

const getProvinceCode = (province: string) => provincesCode[province] || "";

interface Props {
  setNewSite: Dispatch<SetStateAction<boolean>>;
  onAddSite: (_site: TSiteSchema) => void;
  onRemoveSite: () => void;
};

function NewSiteComponent({ setNewSite, onAddSite, onRemoveSite }: Props) {
  const [searchBox, setSearchBox] = useState<google.maps.places.SearchBox | null>(null);
  const [editAddress, setEditAddress] = useState<boolean>(false);
  const [editSite, setEditSite] = useState<boolean>(true);
  const [initial, setInitial] = useState<boolean>(true);
  const [site, setSite] = useState<TSiteSchema>(SiteDefaultValues);
  const [unsavedChanges, setUnsavedChanges] = useState<boolean>(false);

  // Hooks

  const onInvalid = (errors: unknown) => console.error(errors);
  const { t } = useTranslation(["components", "common", "toasts"]);

  const siteInputFields = [
    { name: "city", label: t("newsite.inputs.city.label"), placeholder: t("newsite.inputs.city.placeholder") },
    { name: "country", label: t("newsite.inputs.country.label"), placeholder: t("newsite.inputs.country.placeholder") },
    { name: "postalCode", label: t("newsite.inputs.postalcode.label"), placeholder: t("newsite.inputs.postalcode.placeholder") },
  ];

  // Initialize the form
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    clearErrors,
    reset,
    trigger,
    watch,
    formState: { errors }
  } = useForm<TSiteSchema>({
    resolver: zodResolver(SiteSchema),
    mode: "onChange",
    defaultValues: SiteDefaultValues
  });

  // Load the Google Maps API
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAP_API_KEY as string,
    libraries,
  });

  /**
   * Handler function for when the places in the search box change
   * Extracts the address components from the place and sets the form values
   */
  const onPlacesChanged = () => {
    if (!searchBox) return;

    const places = searchBox.getPlaces();
    if (places!.length === 0) return;

    const place = places![0];
    const addressComponents = place.address_components;

    // Extract the long names of places from the address components
    const getComponent = (type: string) => addressComponents?.find(component => component.types.includes(type))?.long_name || "";

    const streetNumber = getComponent("street_number") || "";
    const route = getComponent("route") || "";
    const address = [streetNumber, route].filter(Boolean).join(" ").trim();

    const administrativeArea = getComponent("administrative_area_level_1");
    const provinceCode = getProvinceCode(administrativeArea);

    setValue("address", address ?? "");
    setValue("city", getComponent("locality") ?? "");
    setValue("province", provinceCode ?? "");
    setValue("country", getComponent("country") ?? "");
    setValue("postalCode", getComponent("postal_code") ?? "");
  };

  /**
   * Handler function for adding a shift to a service
   * Handles logic for both existing and new services
   */
  const onSubmit: SubmitHandler<TSiteSchema> = (data: TSiteSchema) => {
    // Clear form errors
    clearErrors();

    // Call parent function to add site
    onAddSite(data);

    // resetForm();
    toast(CustomToastComponent, {
      toastId: `site-${initial ? "added" : "updated"}-success`,
      data: {
        title: initial ? t("toasts:toasts.siteadded") : t("toasts:toasts.siteupdated"),
        type: "success",
      }
    });
  };

  // Resets Site form only
  const resetForm = () => {
    reset(SiteDefaultValues);
  };

  /**
   * Handler function for the Add/Update button of site
   * Validates the form and calls the onSubmit function
   * @returns {Promise<void>}
   */
  const handleConfirmClick = async () => {
    const isValid = await trigger();
    if (isValid) {
      handleSubmit(onSubmit, onInvalid)();
      setInitial(false);

      // Update site state for changes comparison
      setSite(getValues());
      setEditSite(false);
    }
  };

  /**
   * Handler function for the Remove button of site
   * Sets the newSite state to false and calls the onRemoveSite function from parent
   */
  const handleRemoveClick = () => {
    setNewSite(false);

    // Call parent function to remove site
    onRemoveSite();
    resetForm();
  };

  // Watch all form values
  const watchedValues = watch();

  // Check for unsaved changes
  useEffect(() => {
    const currentValues = getValues();
    if (JSON.stringify(currentValues) !== JSON.stringify(site)) {
      setUnsavedChanges(true);
    } else {
      setUnsavedChanges(false);
    }
  }, [watchedValues, site]);

  return (
    <Card data-cy="new-site-component">
      <div id="add-site-form">
        <CardHeader className="flex flex-row flex-wrap items-center justify-between pb-0">
          <div className="flex flex-col">
            <h2 className="text-lg font-semibold">{t("newsite.title")}</h2>
            <p className="text-gray-500 text-sm">{editSite ? t("newsite.editdescription") : t("newsite.description")}</p>
          </div>
          <div className="flex gap-2">
            {
              !initial && (
                <Button
                  data-cy="edit-site-button"
                  className="mb-4"
                  type="button"
                  variant="outline"
                  onClick={() => {setEditSite(!editSite); setEditAddress(false);}}
                >
                  {editSite ? t("common:buttons.cancel") : t("newsite.btneditsite")}
                </Button>
              )
            }
            <Button
              data-cy="remove-site-button"
              onClick={handleRemoveClick}
              variant={"destructive"}
              className="text-white"
            >
              { t("common:buttons.remove") }
            </Button>
            {
              editSite && (
                <Button
                  type="button"
                  onClick={handleConfirmClick}
                  disabled={!unsavedChanges}
                  data-cy="add-site-button"
                >
                  {initial ? t("common:buttons.add") : t("common:buttons.update")}
                </Button>
              )
            }
          </div>
        </CardHeader>
        <CardContent>
          {/* Display saved new site */}
          {
            !initial && !editSite && (
              <div data-cy="new-site-summary" className="text-sm">
                <span className="font-semibold">{site.name}</span> - <span className="text-gray-500">{appendSiteAddress(site)}</span>
              </div>
            )
          }

          {/* Display new site form */}
          {
            editSite && (
              <React.Fragment>
                <div className="flex-col-4 my-4">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="name" className={cn(errors.name && "text-red-500")}>
                      {t("newsite.inputs.sitename.label")}
                    </Label>
                    <Input
                      {...register("name")}
                      id="name"
                      placeholder={t("newsite.inputs.sitename.placeholder")}
                      className="w-full"
                    />
                    {errors.name && <p className="input-error-message">{errors.name.message}</p>}
                  </div>
                  {isLoaded && (
                    <StandaloneSearchBox onLoad={(ref) => setSearchBox(ref)} onPlacesChanged={onPlacesChanged}>
                      <Input
                        type="text"
                        placeholder={t("newsite.inputs.searchaddress")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                          }
                        }}
                        className="w-full"
                      />
                    </StandaloneSearchBox>
                  )}
                </div>

                <Button className="mb-4" type="button" variant="outline" onClick={() => setEditAddress(!editAddress)}>
                  {editAddress ? t("common:buttons.cancel") : t("newsite.btneditaddress")}
                </Button>

                <div className="flex-wrap-4">
                  {/* Address */}
                  <div key={"address"} className="form-container">
                    <Label htmlFor={"address"} className={cn(errors["address" as keyof TSiteSchema] && "text-red-500")}>
                      {t("newsite.inputs.address.label")}
                    </Label>
                    <Input
                      {...register("address" as keyof TSiteSchema)}
                      id={"address"}
                      placeholder={t("newsite.inputs.address.placeholder")}
                      className="w-full"
                      disabled={!editAddress}
                    />
                    {errors["address" as keyof TSiteSchema] && (
                      <p className="input-error-message">
                        {errors["address" as keyof TSiteSchema]?.message as string}
                      </p>
                    )}
                  </div>

                  {/* Province */}
                  <div key={"province"} className="form-container">
                    <Label htmlFor={"province"} className={cn(errors["province" as keyof TSiteSchema] && "text-red-500")}>
                      {t("newsite.inputs.province.label")}
                    </Label>
                    <Select
                      onValueChange={(value: string) => {
                        setValue("province", value);
                        trigger("province");
                      }}
                      value={watch("province")}
                      disabled={!editAddress}
                    >
                      <SelectTrigger id="province">
                        <SelectValue placeholder={t("newsite.inputs.province.selectplaceholder")} />
                      </SelectTrigger>
                      <SelectContent>
                        {provinces.map((province) => (
                          <SelectItem key={province.code} value={province.code}>
                            {province.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors["province" as keyof TSiteSchema] && (
                      <p className="input-error-message">
                        {errors["province" as keyof TSiteSchema]?.message as string}
                      </p>
                    )}
                  </div>

                  {/* Rest of address fields */}
                  {siteInputFields.map((field) => (
                    <div key={field.name} className="form-container">
                      <Label htmlFor={field.name} className={cn(errors[field.name as keyof TSiteSchema] && "text-red-500")}>
                        {field.label}
                      </Label>
                      <Input
                        {...register(field.name as keyof TSiteSchema)}
                        id={field.name}
                        placeholder={field.placeholder}
                        className="w-full"
                        disabled={!editAddress}
                      />
                      {errors[field.name as keyof TSiteSchema] && (
                        <p className="input-error-message">
                          {errors[field.name as keyof TSiteSchema]?.message as string}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </React.Fragment>
            )
          }
        </CardContent>
      </div>
    </Card>
  );
}

export default NewSiteComponent;
